import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { database } from '../../firebaseConfig';
import { FiCheck, FiCreditCard, FiShield, FiAlertCircle, FiArrowRight, FiHome } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../PageLayout';
import SEO from '../SEO';
import { useTheme } from '../../context/ThemeContext';

const PlansBillingSection = ({ user }) => {
  const [currentPlan, setCurrentPlan] = useState('free');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(null);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  // Load the existing user subscription data if available
  useEffect(() => {
    const loadUserSubscription = async () => {
      try {
        if (!user) return;

        const userRef = doc(database, 'users', user.uid);
        const userDoc = await getDoc(userRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.subscription && userData.subscription.plan) {
            setCurrentPlan(userData.subscription.plan);
          }
        }
      } catch (err) {
        setError('Error loading subscription data.');
        console.error('Error loading subscription data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserSubscription();
  }, [user]);

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '₨0',
      period: 'forever',
      description: 'Basic features for personal use',
      features: [
        'Access till April 30, 2025',
      ],
      gradient: 'from-gray-500 to-gray-600',
      buttonText: 'Current Plan'
    },
    {
      id: 'monthly',
      name: 'Monthly',
      price: '₨499',
      period: 'per month',
      description: 'Everything in Free, plus:',
      features: [
        'Access to 5000+ MCQs',
        'Mock tests from different exams',
        'Detailed self-reports',
        'Step-by-step solutions',
        'Email and chat support'
      ],
      gradient: 'from-blue-500 to-blue-600',
      buttonText: 'Upgrade to Monthly'
    },
    {
      id: 'annual',
      name: 'Annual',
      price: '₨1999',
      period: 'per year',
      description: 'Everything in Monthly, plus:',
      features: [
        'Unlimited access to all MCQs',
        'Priority support',
        'Exclusive mock tests',
        'Advanced analytics',
        'Save 58% compared to monthly'
      ],
      gradient: 'from-purple-600 to-pink-600',
      buttonText: 'Upgrade to Annual',
      popular: true
    }
  ];

  const handleUpgrade = (planId) => {
    if (planId === currentPlan) return;
    console.log(`Upgrading to ${planId} plan`);
    alert(`This would redirect to the checkout page for the ${planId} plan.`);
  };

  const toggleDetails = (planId) => {
    setShowDetails(showDetails === planId ? null : planId);
  };

  // Theme-based classes
  const cardClass = isDark
    ? 'bg-dark-secondary/50 border border-white/10'
    : 'bg-white border border-gray-200 shadow-lg';

  const textClass = isDark ? 'text-white' : 'text-gray-800';
  const subTextClass = isDark ? 'text-gray-400' : 'text-gray-600';

  if (isLoading) {
    return (
      <PageLayout>
        <div className="flex justify-center items-center p-8 min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <div className={`ml-3 ${subTextClass}`}>Loading subscription details...</div>
        </div>
      </PageLayout>
    );
  }

  return (
    <>
      <SEO
        title="Plans & Billing - Prepify | NUST NET Preparation Subscription"
        description="Choose the perfect Prepify plan for your NUST Entry Test preparation."
        keywords="Prepify Plans, NUST Preparation Subscription, NET Test Premium"
        url="/plans-billing"
      />
      <PageLayout>
        <div className="max-w-5xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="mb-6">
            <h2 className={`text-2xl font-bold ${textClass}`}>Plans & Billing</h2>
            <p className={`mt-1 ${subTextClass}`}>Choose the plan that works best for you</p>
          </div>

          {error && (
            <div className={`mb-6 p-4 rounded-xl border-l-4 border-red-500 ${isDark ? 'bg-red-500/10' : 'bg-red-50'}`}>
              <div className="flex items-center">
                <FiAlertCircle className="w-5 h-5 text-red-500 mr-2" />
                <span className="text-red-500">{error}</span>
              </div>
            </div>
          )}

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02] ${plan.id === currentPlan
                    ? 'ring-2 ring-purple-500'
                    : ''
                  } ${cardClass}`}
              >
                {plan.id === currentPlan && (
                  <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg z-10">
                    CURRENT
                  </div>
                )}

                {plan.popular && plan.id !== currentPlan && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg z-10">
                    POPULAR
                  </div>
                )}

                {/* Plan Header */}
                <div className={`bg-gradient-to-br ${plan.gradient} p-5 text-white`}>
                  <h3 className="text-lg font-bold">{plan.name}</h3>
                  <div className="mt-2 flex items-baseline">
                    <span className="text-3xl font-extrabold">{plan.price}</span>
                    <span className="ml-1 text-sm opacity-80">{plan.period}</span>
                  </div>
                  <p className="mt-2 text-sm opacity-90">{plan.description}</p>
                </div>

                {/* Plan Features */}
                <div className="p-5">
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <FiCheck className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className={`text-sm ${subTextClass}`}>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handleUpgrade(plan.id)}
                    className={`w-full mt-5 py-3 px-4 rounded-xl text-sm font-semibold transition duration-300 ${plan.id === currentPlan
                        ? isDark
                          ? 'bg-gray-700 text-gray-400 cursor-default'
                          : 'bg-gray-200 text-gray-500 cursor-default'
                        : `bg-gradient-to-r ${plan.gradient} text-white hover:opacity-90`
                      }`}
                    disabled={plan.id === currentPlan}
                  >
                    {plan.id === currentPlan ? 'Current Plan' : plan.buttonText}
                  </button>

                  <button
                    onClick={() => toggleDetails(plan.id)}
                    className={`w-full mt-2 text-sm font-medium py-2 flex justify-center items-center transition duration-300 ${subTextClass} hover:text-purple-500`}
                  >
                    {showDetails === plan.id ? 'Hide details' : 'Show details'}
                    <FiArrowRight className={`ml-1 transform transition-transform duration-300 ${showDetails === plan.id ? 'rotate-90' : ''}`} />
                  </button>

                  {showDetails === plan.id && (
                    <div className={`mt-3 pt-3 border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                      <h4 className={`font-medium text-sm mb-2 ${textClass}`}>Plan Details:</h4>
                      <div className={`space-y-1 text-xs ${subTextClass}`}>
                        <p>• Billing cycle: {plan.period}</p>
                        <p>• Auto-renews: {plan.id !== 'free' ? 'End of period' : 'N/A'}</p>
                        <p>• Cancellation: {plan.id !== 'free' ? 'Anytime' : 'N/A'}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Payment Method Section */}
          <div className={`rounded-xl p-5 mb-6 ${cardClass}`}>
            <h3 className={`text-lg font-bold mb-4 ${textClass}`}>Payment Method</h3>

            {currentPlan === 'free' ? (
              <div className={`flex items-center py-2 ${subTextClass}`}>
                <FiCreditCard className="w-5 h-5 mr-3 opacity-60" />
                <p>No payment method on file</p>
              </div>
            ) : (
              <div className={`flex items-center py-2 pb-3 border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                <div className="w-10 h-6 bg-blue-600 rounded mr-3 flex items-center justify-center text-white text-xs font-bold">VISA</div>
                <div>
                  <p className={`font-medium ${textClass}`}>Visa ending in 4242</p>
                  <p className={`text-xs ${subTextClass}`}>Expires 09/2027</p>
                </div>
              </div>
            )}

            <div className={`mt-3 text-xs flex items-center ${subTextClass}`}>
              <FiShield className="w-4 h-4 mr-2 opacity-60" />
              <p>Your payment information is stored securely</p>
            </div>
          </div>

          {/* Billing History */}
          <div className={`rounded-xl p-5 ${cardClass}`}>
            <h3 className={`text-lg font-bold mb-4 ${textClass}`}>Billing History</h3>

            {currentPlan === 'free' ? (
              <div className="text-center py-6">
                <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <FiCreditCard className={`w-6 h-6 ${subTextClass}`} />
                </div>
                <h4 className={`font-medium mb-1 ${textClass}`}>No billing history yet</h4>
                <p className={`text-sm max-w-sm mx-auto mb-4 ${subTextClass}`}>
                  Your billing history will appear here once you subscribe
                </p>
                <button
                  className="bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium py-2 px-4 rounded-xl hover:opacity-90 transition duration-300"
                  onClick={() => handleUpgrade('monthly')}
                >
                  Upgrade to Monthly
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className={isDark ? 'bg-dark-tertiary/50' : 'bg-gray-50'}>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${subTextClass}`}>Date</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${subTextClass}`}>Description</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${subTextClass}`}>Amount</th>
                      <th className={`px-4 py-2 text-left text-xs font-medium uppercase ${subTextClass}`}>Status</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-white/10' : 'divide-gray-200'}`}>
                    <tr>
                      <td className={`px-4 py-3 text-sm ${textClass}`}>Feb 28, 2025</td>
                      <td className={`px-4 py-3 text-sm ${textClass}`}>Monthly Plan</td>
                      <td className={`px-4 py-3 text-sm ${textClass}`}>₨499</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-500/20 text-green-500">Paid</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Back Button */}
          <button
            onClick={() => navigate('/')}
            className="w-full mt-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:opacity-90 transition-all font-semibold shadow-lg"
          >
            Back to Dashboard
          </button>
        </div>
      </PageLayout>
    </>
  );
};

export default PlansBillingSection;